// ===================================================
// ФАЙЛ: frontend/src/utils/prefetch.js
// Система предзагрузки данных для быстрых переходов между страницами
// ✅ ОПТИМИЗИРОВАНО: Полностью неблокирующий prefetch с коротким таймаутом
// ===================================================
import axios from './axios';
import { billingApiPath } from './billingApiPaths';

/**
 * Фоновый prefetch не показывает toast пользователю.
 * В development даём видимость в консоли (без шума от abort таймаута).
 */
export function reportPrefetchFailure(context, err) {
  if (process.env.NODE_ENV !== 'development') return;
  const msg = (err?.message || String(err || '')).toLowerCase();
  if (msg.includes('abort') || msg.includes('cancel') || msg.includes('canceled') || msg.includes('aborted')) {
    return;
  }
  console.warn(`[markbase prefetch] ${context}:`, err?.message || err);
}

// Resolve the canonical store lazily: importing it at module initialization would
// introduce a cycle through authSlice -> axios.
let storePromise;
const getAuthStore = () => {
  if (!storePromise) storePromise = import('../store').then((module) => module.store);
  return storePromise;
};
const readyCompanyScope = (store) => {
  const auth = store.getState().auth;
  return auth?.isAuthenticated && auth.bootstrapStatus === 'ready' && auth.user?.id && auth.company?.id
    ? String(auth.user.id) + ':' + String(auth.company.id) : null;
};

const prefetchGet = async (url, config = {}) => {
  const store = await getAuthStore();
  const scope = readyCompanyScope(store);
  if (!scope) return null;
  const controller = new AbortController();
  const unsubscribe = store.subscribe(() => {
    if (readyCompanyScope(store) !== scope) controller.abort();
  });
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await axios.get(url, {
      ...config, prefetch: true, errorPresentation: 'inline',
      cacheTtlMs: 0, noDedup: true, timeout: 5000, signal: controller.signal,
    });
    return readyCompanyScope(store) === scope ? response : null;
  } finally {
    unsubscribe();
    clearTimeout(timeoutId);
  }
};

// Кэш предзагруженных данных
const prefetchCache = new Map();
// Защита от параллельных одинаковых prefetch
const prefetchInFlight = new Map();
const PREFETCH_TTL = 5 * 60 * 1000; // 5 минут
const MAX_PREFETCH_CACHE_ENTRIES = 100; // Защита от утечек памяти при множестве пользователей

// Очистка устаревших данных из кэша
const cleanupPrefetchCache = () => {
  const now = Date.now();
  const entriesToDelete = [];

  // Собираем ключи для удаления
  for (const [key, value] of prefetchCache.entries()) {
    if (now - value.timestamp > PREFETCH_TTL) {
      entriesToDelete.push(key);
    }
  }

  // Удаляем устаревшие записи
  entriesToDelete.forEach(key => prefetchCache.delete(key));

  // Если кэш все еще слишком большой - удаляем самые старые записи
  if (prefetchCache.size > MAX_PREFETCH_CACHE_ENTRIES) {
    const sortedEntries = Array.from(prefetchCache.entries())
      .sort((a, b) => a[1].timestamp - b[1].timestamp);
    const toRemove = sortedEntries.slice(0, prefetchCache.size - MAX_PREFETCH_CACHE_ENTRIES);
    toRemove.forEach(([key]) => prefetchCache.delete(key));
  }
};

// Запускаем очистку каждые 2 минуты
if (typeof window !== 'undefined') {
  setInterval(cleanupPrefetchCache, 2 * 60 * 1000);
}

/**
 * Предзагрузка данных для страницы
 * @param {string} pagePath - путь страницы
 * @param {object} options - опции предзагрузки
 */
export const prefetchPageData = async (pagePath, options = {}) => {
  if (typeof window === 'undefined') return;
  const store = await getAuthStore();
  const scope = readyCompanyScope(store);
  if (!scope) return null;

  const cacheKey = `prefetch:${scope}:${pagePath}`;
  const cached = prefetchCache.get(cacheKey);

  // Если данные уже в кэше и свежие - не загружаем повторно
  if (cached && (Date.now() - cached.timestamp < PREFETCH_TTL)) {
    return cached.data;
  }

  // Если prefetch уже выполняется для этого пути - возвращаем текущий промис
  if (prefetchInFlight.has(cacheKey)) {
    return prefetchInFlight.get(cacheKey);
  }

  const run = (async () => {
    try {
    const promises = [];

    // Предзагрузка данных в зависимости от страницы
    // ✅ Используем только axios напрямую для избежания циклических зависимостей
    switch (pagePath) {
      case '/dashboard':
        promises.push(
          prefetchGet('/api/analytics/dashboard', { cacheTtlMs: 30 * 1000 }),
          prefetchGet('/api/orders', { params: { limit: 5, sort: 'created_at:desc' }, cacheTtlMs: 30 * 1000 }),
          prefetchGet('/api/products', { params: { limit: 5, low_stock: true }, cacheTtlMs: 30 * 1000 }),
          prefetchGet('/api/sync/status', { cacheTtlMs: 2 * 60 * 1000 })
        );
        break;

      case '/products':
        promises.push(
          prefetchGet('/api/dictionaries/brands', { params: { limit: 1000 }, cacheTtlMs: 5 * 60 * 1000 }),
          prefetchGet('/api/dictionaries/warehouses', { cacheTtlMs: 5 * 60 * 1000 }),
          prefetchGet('/api/dictionaries/series', { params: { limit: 5000, offset: 0 }, cacheTtlMs: 5 * 60 * 1000 }),
          prefetchGet('/api/suppliers', { cacheTtlMs: 2 * 60 * 1000 })
        );
        break;

      case '/warehouses':
        promises.push(
          prefetchGet('/api/warehouses', { params: { full: true }, cacheTtlMs: 2 * 60 * 1000 }),
          prefetchGet('/api/dictionaries/warehouses', { cacheTtlMs: 5 * 60 * 1000 })
        );
        break;

      case '/suppliers':
        promises.push(
          prefetchGet('/api/suppliers', { cacheTtlMs: 2 * 60 * 1000 }),
          prefetchGet('/api/dictionaries/warehouses', { cacheTtlMs: 5 * 60 * 1000 })
        );
        break;

      case '/orders':
        promises.push(
          prefetchGet('/api/orders', { params: { limit: 20, sort: 'created_at:desc' }, cacheTtlMs: 30 * 1000 })
        );
        break;

      case '/marketplaces':
      case '/connections':
        // Prefetch legacy MAIN marketplaces list is optional; INTEGRATIONS SPA loads its own API.
        break;

      case '/users':
        // /api/users can be absent depending on UAM deployment mode.
        // Keep users page lazy-loaded to avoid noisy prefetch errors.
        break;

      case '/settings':
        promises.push(
          prefetchGet('/api/settings', { cacheTtlMs: 2 * 60 * 1000 }),
          prefetchGet(billingApiPath('balance'), { cacheTtlMs: 2 * 60 * 1000 })
        );
        break;

      case '/dictionaries':
        promises.push(
          prefetchGet('/api/dictionaries/brands', { params: { limit: 1000 }, cacheTtlMs: 5 * 60 * 1000 }),
          prefetchGet('/api/products/categories', { cacheTtlMs: 5 * 60 * 1000 }),
          prefetchGet('/api/dictionaries/series', { params: { limit: 5000, offset: 0 }, cacheTtlMs: 5 * 60 * 1000 })
        );
        break;
      default:
        // Для неизвестных путей не делаем prefetch
        break;
    }

      if (promises.length > 0) {
        const results = await Promise.allSettled(promises);
        const data = results.map(r => r.status === 'fulfilled' ? r.value : null);

        if (readyCompanyScope(store) !== scope) return null;
        prefetchCache.set(cacheKey, { data, timestamp: Date.now() });

        return data;
      }
    } catch (error) {
      // Тихая обработка ошибок - prefetch не должен ломать приложение
      console.debug('Prefetch error for', pagePath, error);
    } finally {
      prefetchInFlight.delete(cacheKey);
    }

    return null;
  })();

  prefetchInFlight.set(cacheKey, run);
  return run;
};

/**
 * Настройка автоматической предзагрузки при наведении на ссылки
 */
export const setupLinkPrefetching = () => {
  if (typeof window === 'undefined') return;

  // ✅ ЗАЩИТА: Оборачиваем в try-catch для предотвращения ошибок инициализации
  try {
    const handleLinkHover = (event) => {
    const target = event.target;
    if (!target) return;

    // ✅ ИСПРАВЛЕНО: Проверяем что target является DOM элементом перед вызовом closest
    // event.target может быть текстовым узлом или другим объектом без метода closest
    if (typeof target.closest !== 'function') {
      // Если target не DOM элемент, пытаемся найти родительский элемент
      if (target.parentElement && typeof target.parentElement.closest === 'function') {
        const parent = target.parentElement;
        let linkElement = parent.closest('a');
        if (!linkElement) {
          linkElement = parent.closest('[data-href]') || parent.closest('[data-path]');
        }
        if (!linkElement) return;

        const href = linkElement.getAttribute('href') ||
                     linkElement.getAttribute('data-href') ||
                     linkElement.getAttribute('data-path');

        // eslint-disable-next-line no-script-url
        if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
          return;
        }

        // Извлекаем путь из href
        try {
          const url = new URL(href, window.location.origin);
          const path = url.pathname;

          if (path && path !== window.location.pathname) {
            setTimeout(() => {
              prefetchPageData(path).catch((e) => reportPrefetchFailure(`hover ${path}`, e));
            }, 300);
          }
        } catch (e) {
          if (href.startsWith('/')) {
            setTimeout(() => {
              prefetchPageData(href).catch((e) => reportPrefetchFailure(`hover ${href}`, e));
            }, 300);
          }
        }
        return;
      }
      return;
    }

    // Ищем ближайший элемент <a> или элемент с data-href/data-path
    let linkElement = target.closest('a');
    if (!linkElement) {
      linkElement = target.closest('[data-href]') || target.closest('[data-path]');
    }

    if (!linkElement) return;

    const href = linkElement.getAttribute('href') ||
                 linkElement.getAttribute('data-href') ||
                 linkElement.getAttribute('data-path');

    // eslint-disable-next-line no-script-url
    if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return;
    }

    // Извлекаем путь из href
    try {
      const url = new URL(href, window.location.origin);
      const path = url.pathname;

      if (path && path !== window.location.pathname) {
        // Предзагружаем данные с небольшой задержкой
        setTimeout(() => {
          prefetchPageData(path).catch((e) => reportPrefetchFailure(`hover ${path}`, e));
        }, 300);
      }
    } catch (e) {
      // Если href не является полным URL, используем его как путь
      if (href.startsWith('/')) {
        setTimeout(() => {
          prefetchPageData(href).catch((e) => reportPrefetchFailure(`hover ${href}`, e));
        }, 300);
      }
    }
  };

    document.addEventListener('mouseenter', handleLinkHover, true);

    return () => {
      document.removeEventListener('mouseenter', handleLinkHover, true);
    };
  } catch (error) {
    // ✅ ЗАЩИТА: Логируем ошибку, но не ломаем приложение
    console.debug('setupLinkPrefetching initialization error:', error);
    // Возвращаем пустую функцию для cleanup
    return () => {};
  }
};

/**
 * Предзагрузка критичных данных при загрузке приложения
 */
export const prefetchCriticalData = async () => {
  if (typeof window === 'undefined') return;

  const store = await getAuthStore();
  const waitForCompany = () => new Promise((resolve) => {
    let unsubscribe = () => {};
    let timeoutId;
    let settled = false;
    const finish = (scope) => {
      if (settled) return;
      settled = true;
      unsubscribe();
      clearTimeout(timeoutId);
      resolve(scope);
    };
    const check = () => {
      const auth = store.getState().auth;
      if (auth.bootstrapStatus === 'ready') finish(readyCompanyScope(store));
      else if (auth.bootstrapStatus === 'idle' || auth.bootstrapStatus === 'error') finish(null);
    };
    unsubscribe = store.subscribe(check);
    timeoutId = setTimeout(() => finish(null), 12000);
    check();
  });
  const scope = await waitForCompany();
  if (!scope || readyCompanyScope(store) !== scope) return;

  try {
    const currentPath = window.location.pathname;
    if (currentPath && currentPath !== '/login' && currentPath !== '/register') {
      await prefetchPageData(currentPath).catch((e) => reportPrefetchFailure(`critical path ${currentPath}`, e));
    }

    if (readyCompanyScope(store) !== scope) return;

    // Предзагружаем критичные справочники
      await Promise.allSettled([
        prefetchGet('/api/dictionaries/warehouses', { cacheTtlMs: 5 * 60 * 1000 }),
        prefetchGet('/api/dictionaries/brands', { params: { limit: 1000 }, cacheTtlMs: 5 * 60 * 1000 }),
        prefetchGet('/api/users/favorites/menu', { cacheTtlMs: 5 * 60 * 1000, silent401: true })
      ]);
  } catch (error) {
    // Тихая обработка ошибок - prefetch не должен ломать приложение
    console.debug('Critical prefetch error:', error);
  }
};
